const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const workoutAPI = {
  // ===== EXERCISES =====

  fetchExercises: async (includeArchived = false) => {
    const url = includeArchived
      ? `${API_URL}/api/workouts/exercises?include_archived=true`
      : `${API_URL}/api/workouts/exercises`;

    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch exercises');
    }
    return response.json();
  },

  createExercise: async (exerciseData) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(exerciseData)
    });
    if (!response.ok) {
      throw new Error('Failed to create exercise');
    }
    return response.json();
  },

  updateExercise: async (exerciseId, exerciseData) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(exerciseData)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.code === 'TYPE_CHANGE_FORBIDDEN') {
        const error = new Error(errorData.message || 'Cannot change exercise type');
        error.code = 'TYPE_CHANGE_FORBIDDEN';
        error.details = errorData;
        throw error;
      }
      throw new Error('Failed to update exercise');
    }
    return response.json();
  },

  archiveExercise: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/archive`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to archive exercise');
    }
    return response.json();
  },

  unarchiveExercise: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/unarchive`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to unarchive exercise');
    }
    return response.json();
  },

  fetchArchivedExercises: async () => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/archived`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch archived exercises');
    }
    return response.json();
  },

  checkExerciseHasLogs: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/has-logs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to check exercise logs');
    }
    return response.json();
  },

  // ===== TEMPLATES =====

  fetchTemplates: async () => {
    const response = await fetch(`${API_URL}/api/workouts/templates`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }
    return response.json();
  },

  fetchTemplateById: async (templateId) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }
    return response.json();
  },

  createTemplate: async (templateData) => {
    const response = await fetch(`${API_URL}/api/workouts/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(templateData)
    });
    if (!response.ok) {
      throw new Error('Failed to create template');
    }
    return response.json();
  },

  updateTemplate: async (templateId, templateData) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(templateData)
    });
    if (!response.ok) {
      throw new Error('Failed to update template');
    }
    return response.json();
  },

  deleteTemplate: async (templateId) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete template');
    }
    return response.json();
  },

  // ===== PROGRAMS =====

  fetchPrograms: async () => {
    const response = await fetch(`${API_URL}/api/workouts/programs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch programs');
    }
    return response.json();
  },

  fetchProgramById: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch program');
    }
    return response.json();
  },

  createProgram: async (programData) => {
    const response = await fetch(`${API_URL}/api/workouts/programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(programData)
    });
    if (!response.ok) {
      throw new Error('Failed to create program');
    }
    return response.json();
  },

  updateProgram: async (programId, programData) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(programData)
    });
    if (!response.ok) {
      throw new Error('Failed to update program');
    }
    return response.json();
  },

  deleteProgram: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete program');
    }
    return response.json();
  },

  // ===== ACTIVE PROGRAMS =====

  fetchActivePrograms: async () => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch active programs');
    }
    return response.json();
  },

  startProgram: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ program_id: programId })
    });
    if (!response.ok) {
      throw new Error('Failed to start program');
    }
    return response.json();
  },

  updateProgramProgress: async (activeProgramId, currentWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/progress`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ current_workout_id: currentWorkoutId })
    });
    if (!response.ok) {
      throw new Error('Failed to update program progress');
    }
    return response.json();
  },

  fetchNextWorkout: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}/next-workout`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch next workout');
    }
    return response.json();
  },

  // ===== WORKOUT LOGS =====

  logWorkout: async (workoutData) => {
    const response = await fetch(`${API_URL}/api/workouts/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(workoutData)
    });
    if (!response.ok) {
      throw new Error('Failed to log workout');
    }
    return response.json();
  },

  fetchWorkoutLogs: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const url = `${API_URL}/api/workouts/logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workout logs');
    }
    return response.json();
  },

  fetchWorkoutLogById: async (workoutLogId) => {
    const response = await fetch(`${API_URL}/api/workouts/logs/${workoutLogId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workout log');
    }
    return response.json();
  },

  fetchExerciseHistory: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercise-history/${exerciseId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch exercise history');
    }
    return response.json();
  },

  // ===== ANALYTICS =====

  fetchPersonalRecords: async () => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/personal-records`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch personal records');
    }
    return response.json();
  },

  fetchExerciseProgressData: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/progress/${exerciseId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch progress data');
    }
    return response.json();
  },

  fetchCompletionStats: async () => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/completion`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch completion stats');
    }
    return response.json();
  },

  // ===== WORKOUT DRAFTS =====

  saveDraft: async (draftData) => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(draftData)
    });
    if (!response.ok) {
      throw new Error('Failed to save draft');
    }
    return response.json();
  },

  loadDraft: async () => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to load draft');
    }
    return response.json();
  },

  clearDraft: async () => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to clear draft');
    }
    return response.json();
  }
};
